/**
 * Warp filters — "liquify" mesh deformation (턱쭉/long-chin, 홀쭉이/skinny,
 * 볼빵빵/puffy) rendered on a PixiJS v8 MeshPlane. Lives inside PenaltyStage's
 * stage box (client/penalty.ts), sandwiched between the (invisible-but-
 * still-playing) `<video>` and the overlay `<canvas>`: a lazily-created,
 * small `Application` whose canvas is only shown/rendered while a warp
 * filter is the active selection.
 *
 * PenaltyStage owns the render cadence — this class never runs its own rAF
 * or Pixi ticker (`autoStart:false`); `renderFrame()` is called once per
 * PenaltyStage tick, and `render()` is called again, synchronously, right
 * before a snapshot reads pixels off `canvasEl` (see `Application#render()`'s
 * own docs for this exact "manual render loop" pattern).
 *
 * COORDINATE SPACE (bug fix — see class doc history): all vertex/anchor math
 * in this file happens in the pixi Application's own LOGICAL/CSS-pixel space
 * (`app.screen.width/height`, i.e. `resizeTo` target's clientWidth/Height) —
 * the SAME space `render.ts`'s GameRenderer.layout() uses. It is NOT the same
 * space as PenaltyStage's 2D overlay `canvas.width/height`, which are BACKING
 * pixels (`clientWidth * dpr`). `autoDensity`+`resolution` make the pixi
 * canvas's backing store `screen size * resolution` automatically — mixing
 * that backing size into the mesh's own vertex math (as an earlier version of
 * this file did) double-applies the DPR scale and crops/zooms the mesh on
 * any device with devicePixelRatio > 1. `computeCoverMap()` is therefore
 * always called here with `this.app.screen.width/height`, never with the
 * overlay canvas's backing `canvas.width/height`.
 *
 * For the snapshot path (captureSnapshotBlob() in penalty.ts): the warp
 * canvas's BACKING store is `screen size * resolution` physical pixels
 * representing that same full logical box, at whatever resolution this
 * Application was given (capped at DPR_CAP, independent of the 2D overlay
 * canvas's own DPR cap). `drawImage(warpCanvas, 0, 0, off.width, off.height)`
 * blits the ENTIRE source canvas (a full representation of the stage box,
 * regardless of its exact backing pixel count) stretched to fill the
 * destination rect — canvas-to-canvas drawImage scales by content, not pixel
 * count, so a resolution mismatch between the two canvases never misaligns
 * the result, only its relative sharpness.
 *
 * Mesh: a single ~32x24 MeshPlane grid. Its UV buffer never changes; every
 * frame this class rewrites the POSITION buffer directly (in the hot vertex
 * loop, inlined and allocation-free — see renderFrame()):
 *   position = coverFitMirrored(u, v) + displacement(filter, landmarks)
 * so the video ends up mirrored for a self-view WITHOUT any CSS transform on
 * this canvas — the mirroring is baked into where each vertex samples its
 * (fixed) UV from.
 *
 * Lifecycle safety: `ensure()`'s `create()` is async (awaits `app.init()`);
 * `destroy()` can land while that await is in flight (fast rematch / re-enter
 * try-on). A `stopped` flag makes that race safe — `create()` checks it right
 * after the await and discards (destroys) the just-created Application
 * instead of publishing it, so a torn-down stage can never end up with a
 * leaked, invisible GL context + canvas.
 *
 * WebGL context loss (iOS realistically hits this: 2 warp Applications ever
 * alive across winner/loser + 2 concurrent MediaPipe GPU delegates): a
 * `webglcontextlost` listener flips `lost`, which `ready` folds in. No
 * restore is attempted (no `preventDefault()`, no `webglcontextrestored`
 * handling) — PenaltyStage.frame() polls `ready` every tick and already
 * treats "not ready" as "show the plain video instead", so losing the
 * context simply (and promptly) degrades this filter to a no-op for the
 * rest of the stage session.
 */

import { Application, MeshPlane, Texture } from "pixi.js";
import type { NormalizedLandmark } from "@mediapipe/tasks-vision";
import type { WarpFilterId } from "./types.js";

export interface WarpRendererOptions {
  /** The PenaltyStage element group's `container` — also the pixi Application's `resizeTo` target. */
  container: HTMLElement;
  /** The overlay 2D `canvas` — the warp canvas is inserted immediately before it (video, warp, overlay, hint). */
  before: HTMLElement;
  video: HTMLVideoElement;
}

interface CoverMap {
  scale: number;
  offX: number;
  offY: number;
  vw: number;
  vh: number;
}

const GRID_X = 32;
const GRID_Y = 24;
/** Matches the rest of the app's backing-resolution cap (render.ts/penalty.ts). */
const DPR_CAP = 1.5;
const EMA_ALPHA = 0.4;
/** Once landmarks go null (post-grace, real face-loss), ease displacement to 0 over this long. */
const IDENTITY_EASE_MS = 300;
/** Per-filter displacement magnitude clamp, as a fraction of that filter's own falloff radius.
 *  This bounds how EXTREME the effect can get; it does NOT by itself prevent adjacent mesh rows
 *  from crossing (see ROW_GAP_FRAC below for the actual anti-fold-over guard). */
const MAX_DISPLACEMENT_FRAC = 0.5;
/** Minimum vertical gap enforced between a vertex and the one directly above it in its column,
 *  as a fraction of the grid's natural row spacing — prevents long-chin's downward push (or any
 *  future warp) from folding the mesh by pushing a row past the row below it. */
const ROW_GAP_FRAC = 0.5;
/** Below this fraction of faceHeightPx, the skinny filter's tilt axis (noseBridge<->chin) is
 *  treated as degenerate (near-horizontal head tilt) and falls back to a vertical axis at chin.x. */
const AXIS_DEGENERACY_FRAC = 0.05;

const LONG_CHIN_R_FRAC = 0.55;
const LONG_CHIN_PUSH_FRAC = 0.45;
const SKINNY_R_FRAC = 0.5;
const SKINNY_PULL_FRAC = 0.28;
const PUFFY_R_FRAC = 0.42;
/** Reduced 0.30 → 0.21 (-30%) per user feedback: the original bulge read as too strong. */
const PUFFY_BULGE_FRAC = 0.21;

/** face.ts's mesh topology — indices from MediaPipe's canonical 478-point face model. */
const LM = {
  chin: 152,
  foreheadTop: 10,
  noseBridge: 168,
  mouthL: 61,
  mouthR: 291,
  ovalL: 234,
  ovalR: 454,
  cheekL: 205,
  cheekR: 425,
} as const;

interface Pt {
  x: number;
  y: number;
}

interface FaceAnchors {
  chin: Pt;
  mouthMid: Pt;
  noseBridge: Pt;
  ovalL: Pt;
  ovalR: Pt;
  cheekL: Pt;
  cheekR: Pt;
  faceHeightPx: number;
  faceWidthPx: number;
}

/** Same cover-fit math as PenaltyStage's computeCoverMap (duplicated, see class doc) — only used
 *  off the hot path (once/frame for anchors, not per-vertex; the vertex loop inlines this itself). */
function computeCoverMap(video: HTMLVideoElement, canvasW: number, canvasH: number): CoverMap {
  const sw = canvasW || 1;
  const sh = canvasH || 1;
  const vw = video.videoWidth > 0 ? video.videoWidth : sw;
  const vh = video.videoHeight > 0 ? video.videoHeight : sh;
  const scale = Math.max(sw / vw, sh / vh);
  return { scale, offX: (sw - vw * scale) / 2, offY: (sh - vh * scale) / 2, vw, vh };
}

/** Map a normalized (unmirrored) video-space point to canvas pixels (off the hot path — see above). */
function mapPoint(m: CoverMap, vx: number, vy: number, mirrored: boolean): Pt {
  const dispW = m.vw * m.scale;
  const dispH = m.vh * m.scale;
  return { x: m.offX + dispW * (mirrored ? 1 - vx : vx), y: m.offY + dispH * vy };
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Smooth "liquify brush" falloff: 1 at the anchor, 0 at/beyond radius r. */
function falloff(d: number, r: number): number {
  if (r <= 0 || d >= r) return 0;
  const t = 1 - (d / r) * (d / r);
  return t * t;
}

export class WarpRenderer {
  private app: Application | null = null;
  private plane: MeshPlane | null = null;
  private creating: Promise<void> | null = null;
  /** Set by destroy() — makes the async create() a no-op (destroy-and-discard) if it lands after. */
  private stopped = false;
  /** Set once by the webglcontextlost listener; folded into `ready`. No restore is attempted. */
  private lost = false;
  private filter: WarpFilterId | null = null;
  private lastLm: NormalizedLandmark[] | null = null;
  /** 1 = full displacement; eased to 0 over IDENTITY_EASE_MS once the face is truly lost. */
  private identityBlend = 1;
  private readonly smoothState = new Map<string, number>();
  /** Fixed UV grid, precomputed once — matches PlaneGeometry's own default (u=col/(X-1), v=row/(Y-1)). */
  private readonly gridUV: Pt[] = [];
  /** Reused every vertex in the hot loop instead of allocating a fresh {x,y} per call. */
  private readonly dispScratch: Pt = { x: 0, y: 0 };

  private readonly handleContextLost = (): void => {
    this.lost = true;
    console.warn("[warp] WebGL context lost — degrading this stage session to plain video (no restore attempted).");
  };

  constructor(private readonly opts: WarpRendererOptions) {
    for (let row = 0; row < GRID_Y; row++) {
      for (let col = 0; col < GRID_X; col++) {
        this.gridUV.push({ x: col / (GRID_X - 1), y: row / (GRID_Y - 1) });
      }
    }
  }

  /** False while still initializing, once destroyed, or after a WebGL context loss. */
  get ready(): boolean {
    return this.app !== null && !this.lost;
  }

  get canvasEl(): HTMLCanvasElement | null {
    return (this.app?.canvas as HTMLCanvasElement | undefined) ?? null;
  }

  setFilter(filter: WarpFilterId | null): void {
    if (filter !== this.filter) {
      this.smoothState.clear();
      this.identityBlend = 1;
      this.lastLm = null;
    }
    this.filter = filter;
  }

  setVisible(visible: boolean): void {
    const canvas = this.canvasEl;
    if (canvas) canvas.style.display = visible ? "block" : "none";
  }

  /** Lazily stands up the Application + mesh on first warp selection. Idempotent and re-entrant safe. */
  async ensure(): Promise<void> {
    if (this.stopped || this.app) return;
    if (!this.creating) this.creating = this.create();
    return this.creating;
  }

  private async create(): Promise<void> {
    const app = new Application();
    await app.init({
      resizeTo: this.opts.container,
      backgroundAlpha: 0,
      resolution: Math.min(window.devicePixelRatio || 1, DPR_CAP),
      autoDensity: true,
      autoStart: false, // PenaltyStage's own rAF drives renderFrame()/render() — see class doc.
    });
    if (this.stopped) {
      // destroy() landed while we were awaiting init() — don't publish a live
      // canvas/GL context into a container nobody is going to tear down.
      app.destroy(true, { children: true, texture: false, textureSource: false });
      return;
    }
    const canvas = app.canvas as HTMLCanvasElement;
    canvas.style.position = "absolute";
    canvas.style.inset = "0";
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.display = "none"; // PenaltyStage.frame() flips this via setVisible() each tick.
    canvas.addEventListener("webglcontextlost", this.handleContextLost, false);
    this.opts.container.insertBefore(canvas, this.opts.before);

    const plane = new MeshPlane({ texture: Texture.from(this.opts.video), verticesX: GRID_X, verticesY: GRID_Y });
    plane.autoResize = false; // we fully own vertex positions every frame regardless of texture size
    app.stage.addChild(plane);

    this.app = app;
    this.plane = plane;
  }

  /** Force a GPU render of the mesh's current buffer state (no vertex recompute) — used before a snapshot read. */
  render(): void {
    if (this.lost) return;
    this.app?.render();
  }

  /** Recompute the mesh's vertex buffer from the latest landmarks and render. Call once per PenaltyStage tick. */
  renderFrame(lm: NormalizedLandmark[] | null, mirrored: boolean, dtMs: number): void {
    if (!this.app || !this.plane || !this.filter || this.lost) return;
    if (lm) this.lastLm = lm;

    const target = lm ? 1 : 0;
    const rate = Math.min(1, dtMs / IDENTITY_EASE_MS);
    this.identityBlend =
      target > this.identityBlend ? Math.min(1, this.identityBlend + rate) : Math.max(0, this.identityBlend - rate);

    // Logical/CSS-px cover map — see the class doc's COORDINATE SPACE note.
    // `app.screen` is the pixi Application's logical size, matching how
    // render.ts's GameRenderer.layout() reads `renderer.screen`.
    const screen = this.app.screen;
    const cover = computeCoverMap(this.opts.video, screen.width, screen.height);
    const anchors = this.lastLm ? this.computeAnchors(this.lastLm, cover, mirrored) : null;
    const blend = this.identityBlend;
    const useDisp = anchors !== null && blend > 0;
    const filter = this.filter;

    const attr = this.plane.geometry.getAttribute("aPosition");
    const data = attr.buffer.data;
    const dispW = cover.vw * cover.scale;
    const dispH = cover.vh * cover.scale;
    const rowSpacing = dispH / (GRID_Y - 1);
    const minGap = rowSpacing * ROW_GAP_FRAC;

    for (let i = 0; i < this.gridUV.length; i++) {
      const grid = this.gridUV[i];
      if (!grid) continue;
      // Inlined mapPoint (base cover-fit position) — no per-vertex allocation.
      const bx = cover.offX + dispW * (mirrored ? 1 - grid.x : grid.x);
      let y = cover.offY + dispH * grid.y;
      let x = bx;

      if (useDisp && anchors) {
        this.computeDisplacement(filter, anchors, bx, y);
        x = bx + this.dispScratch.x * blend;
        y = y + this.dispScratch.y * blend;
      }

      // Anti-fold-over guard: never let a row end up above (smaller y than)
      // the row directly above it in the same column, minus a small margin.
      // Row-major layout (col fastest) means that vertex is always already
      // written earlier in this same pass — see PlaneGeometry.build().
      const col = i % GRID_X;
      const row = (i - col) / GRID_X;
      if (row > 0) {
        const aboveY = data[(i - GRID_X) * 2 + 1] ?? y;
        const minY = aboveY + minGap;
        if (y < minY) y = minY;
      }

      data[i * 2] = x;
      data[i * 2 + 1] = y;
    }
    attr.buffer.update();
    this.app.render();
  }

  /** Frees the WebGL context — call once per stage session (PenaltyStage.stop()). Idempotent, and
   *  safe to call while ensure()/create() is still in flight (see create()'s `stopped` check). */
  destroy(): void {
    this.stopped = true;
    this.creating = null;
    if (!this.app) return;
    const canvas = this.app.canvas as HTMLCanvasElement;
    canvas.removeEventListener("webglcontextlost", this.handleContextLost);
    // Spare the shared video texture/source: the same <video> element is
    // reused across stage sessions (rematches, re-entering try-on), and a
    // TextureSource isn't tied to any one renderer in v8 — only THIS small
    // Application's canvas/GL context needs to go.
    this.app.destroy(true, { children: true, texture: false, textureSource: false });
    this.app = null;
    this.plane = null;
  }

  private smooth(key: string, next: number): number {
    const prev = this.smoothState.get(key);
    const v = prev === undefined ? next : prev + (next - prev) * EMA_ALPHA;
    this.smoothState.set(key, v);
    return v;
  }

  private computeAnchors(lm: NormalizedLandmark[], cover: CoverMap, mirrored: boolean): FaceAnchors | null {
    const pt = (i: number): Pt | null => {
      const p = lm[i];
      return p ? mapPoint(cover, p.x, p.y, mirrored) : null;
    };
    const chin = pt(LM.chin);
    const foreheadTop = pt(LM.foreheadTop);
    const noseBridge = pt(LM.noseBridge);
    const mouthL = pt(LM.mouthL);
    const mouthR = pt(LM.mouthR);
    const ovalL = pt(LM.ovalL);
    const ovalR = pt(LM.ovalR);
    const cheekL = pt(LM.cheekL);
    const cheekR = pt(LM.cheekR);
    if (!chin || !foreheadTop || !noseBridge || !mouthL || !mouthR || !ovalL || !ovalR || !cheekL || !cheekR) {
      return null;
    }
    const faceHeightPx = this.smooth("faceH", Math.hypot(chin.x - foreheadTop.x, chin.y - foreheadTop.y));
    const faceWidthPx = this.smooth("faceW", Math.hypot(ovalR.x - ovalL.x, ovalR.y - ovalL.y));
    return {
      chin: { x: this.smooth("chin-x", chin.x), y: this.smooth("chin-y", chin.y) },
      mouthMid: {
        x: this.smooth("mouth-x", (mouthL.x + mouthR.x) / 2),
        y: this.smooth("mouth-y", (mouthL.y + mouthR.y) / 2),
      },
      noseBridge: { x: this.smooth("nb-x", noseBridge.x), y: this.smooth("nb-y", noseBridge.y) },
      ovalL: { x: this.smooth("ol-x", ovalL.x), y: this.smooth("ol-y", ovalL.y) },
      ovalR: { x: this.smooth("or-x", ovalR.x), y: this.smooth("or-y", ovalR.y) },
      cheekL: { x: this.smooth("cl-x", cheekL.x), y: this.smooth("cl-y", cheekL.y) },
      cheekR: { x: this.smooth("cr-x", cheekR.x), y: this.smooth("cr-y", cheekR.y) },
      faceHeightPx,
      faceWidthPx,
    };
  }

  /**
   * Per-vertex displacement for the active warp filter, written into the
   * reused `this.dispScratch` (NOT returned as a fresh object — this runs
   * ~768x/frame, see renderFrame()'s zero-allocation hot loop). All
   * distances scale off the (smoothed) face size so the effect holds
   * regardless of how close the face is to the camera:
   *  - long-chin: pushes vertices below the mouth line straight down, biased
   *    in from 0 at the mouth line to 1 at the chin so lips/nose stay put.
   *  - skinny: pulls vertices near each face-oval side point horizontally
   *    toward the tilt-aware vertical centerline (noseBridge<->chin), falling
   *    back to a straight vertical axis at chin.x if that line is nearly
   *    horizontal (extreme head tilt would otherwise blow up the slope).
   *  - puffy: bulges vertices near each cheek center radially outward.
   * All three clamp their own magnitude to MAX_DISPLACEMENT_FRAC x radius —
   * this bounds how extreme a single vertex's displacement gets, but does
   * NOT by itself stop adjacent rows from crossing; renderFrame()'s row-gap
   * guard is what actually prevents mesh fold-over.
   */
  private computeDisplacement(filter: WarpFilterId, a: FaceAnchors, x: number, y: number): void {
    const out = this.dispScratch;
    switch (filter) {
      case "long-chin": {
        const r = LONG_CHIN_R_FRAC * a.faceHeightPx;
        if (y < a.mouthMid.y) {
          out.x = 0;
          out.y = 0;
          return;
        }
        const ddx = x - a.chin.x;
        const ddy = y - a.chin.y;
        const d = Math.sqrt(ddx * ddx + ddy * ddy);
        const w = falloff(d, r);
        if (w === 0) {
          out.x = 0;
          out.y = 0;
          return;
        }
        const span = a.chin.y - a.mouthMid.y;
        const bias = span > 0 ? clamp01((y - a.mouthMid.y) / span) : 1;
        const dy = LONG_CHIN_PUSH_FRAC * a.faceHeightPx * w * bias;
        const maxLen = MAX_DISPLACEMENT_FRAC * r;
        out.x = 0;
        out.y = Math.abs(dy) > maxLen ? Math.sign(dy) * maxLen : dy;
        return;
      }
      case "skinny": {
        const r = SKINNY_R_FRAC * a.faceHeightPx;
        const axisSpan = a.chin.y - a.noseBridge.y;
        const dx = this.pullTowardAxis(x, y, a.ovalL.x, a.ovalL.y, r, a, axisSpan) +
          this.pullTowardAxis(x, y, a.ovalR.x, a.ovalR.y, r, a, axisSpan);
        const maxLen = MAX_DISPLACEMENT_FRAC * r;
        out.x = Math.abs(dx) > maxLen ? Math.sign(dx) * maxLen : dx;
        out.y = 0;
        return;
      }
      case "puffy": {
        const r = PUFFY_R_FRAC * a.faceHeightPx;
        let dx = 0;
        let dy = 0;
        {
          const vx = x - a.cheekL.x;
          const vy = y - a.cheekL.y;
          const d = Math.sqrt(vx * vx + vy * vy);
          if (d !== 0) {
            const w = falloff(d, r);
            if (w !== 0) {
              const mag = (PUFFY_BULGE_FRAC * a.faceWidthPx * w) / d;
              dx += vx * mag;
              dy += vy * mag;
            }
          }
        }
        {
          const vx = x - a.cheekR.x;
          const vy = y - a.cheekR.y;
          const d = Math.sqrt(vx * vx + vy * vy);
          if (d !== 0) {
            const w = falloff(d, r);
            if (w !== 0) {
              const mag = (PUFFY_BULGE_FRAC * a.faceWidthPx * w) / d;
              dx += vx * mag;
              dy += vy * mag;
            }
          }
        }
        const len = Math.sqrt(dx * dx + dy * dy);
        const maxLen = MAX_DISPLACEMENT_FRAC * r;
        if (len > maxLen && len > 0) {
          const k = maxLen / len;
          dx *= k;
          dy *= k;
        }
        out.x = dx;
        out.y = dy;
        return;
      }
    }
  }

  /** One side's pull-toward-centerline contribution for "skinny". A plain method (not a per-vertex
   *  closure) so calling it twice per vertex allocates nothing beyond its own primitive locals. */
  private pullTowardAxis(
    x: number,
    y: number,
    sx: number,
    sy: number,
    r: number,
    a: FaceAnchors,
    axisSpan: number,
  ): number {
    const ddx = x - sx;
    const ddy = y - sy;
    const d = Math.sqrt(ddx * ddx + ddy * ddy);
    const w = falloff(d, r);
    if (w === 0) return 0;
    const axisX =
      Math.abs(axisSpan) < a.faceHeightPx * AXIS_DEGENERACY_FRAC
        ? a.chin.x // near-horizontal chin<->noseBridge line (extreme tilt) — fall back to a vertical axis
        : a.noseBridge.x + (a.chin.x - a.noseBridge.x) * ((y - a.noseBridge.y) / axisSpan);
    return (axisX > x ? 1 : -1) * SKINNY_PULL_FRAC * a.faceWidthPx * w;
  }
}
